import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../integrations/supabase/client';

const { FiMapPin, FiShoppingBag, FiTruck, FiPhone, FiGlobe, FiStar } = FiIcons;

const Distribution = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [content, setContent] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      const { data } = await supabase.from('page_content').select('*').eq('page', 'distribution');
      if (data) {
        const contentMap = {};
        data.forEach(item => {
          contentMap[`${item.section}.${item.key}`] = isRTL ? (item.content_ar || item.content_en) : item.content_en;
        });
        setContent(contentMap);
      }
    };
    fetchContent();
  }, [isRTL]);

  const getText = (key, fallbackKey) => content?.[key] || t(fallbackKey);

  const regions = [
    {
      name: "Greater Cairo",
      nameAr: "القاهرة الكبرى",
      cities: ["Cairo", "Giza", "Qalyubia"],
      stores: 150,
      color: "from-green-500 to-green-600"
    },
    {
      name: "Alexandria",
      nameAr: "الإسكندرية",
      cities: ["Alexandria", "Beheira", "Matrouh"],
      stores: 85,
      color: "from-blue-500 to-blue-600"
    },
    {
      name: "Delta Region",
      nameAr: "منطقة الدلتا",
      cities: ["Dakahlia", "Gharbia", "Kafr El Sheikh", "Damietta"],
      stores: 120,
      color: "from-amber-500 to-amber-600"
    },
    {
      name: "Upper Egypt",
      nameAr: "صعيد مصر",
      cities: ["Assiut", "Sohag", "Luxor", "Aswan"],
      stores: 95,
      color: "from-red-500 to-red-600"
    },
    {
      name: "Canal Cities",
      nameAr: "مدن القناة",
      cities: ["Suez", "Ismailia", "Port Said"],
      stores: 45,
      color: "from-purple-500 to-purple-600"
    },
    {
      name: "Red Sea",
      nameAr: "البحر الأحمر",
      cities: ["Hurghada", "Marsa Alam", "Safaga"],
      stores: 25,
      color: "from-teal-500 to-teal-600"
    }
  ];

  const storeTypes = [
    {
      icon: FiShoppingBag,
      title: isRTL ? "السوبر ماركت" : "Supermarkets",
      titleEn: "Supermarkets",
      description: isRTL ? "السلاسل الكبرى بما في ذلك كارفور وسبينيز وميتو" : "Major chains including Carrefour, Spinneys, and Metro",
      descriptionEn: "Major chains including Carrefour, Spinneys, and Metro",
      count: "200+ locations"
    },
    {
      icon: FiStar,
      title: isRTL ? "المتاجر الفاخرة" : "Premium Stores",
      titleEn: "Premium Stores",
      description: isRTL ? "متاجر القهوة المتخصصة ومتاجر الأغذية الفاخرة" : "Specialty coffee shops and gourmet food stores",
      descriptionEn: "Specialty coffee shops and gourmet food stores",
      count: "150+ locations"
    },
    {
      icon: FiTruck,
      title: isRTL ? "الموزعين" : "Distributors",
      titleEn: "Distributors",
      description: isRTL ? "شركاء توزيع نكست سابلاي المعتمدون" : "Authorized Next Supply distribution partners",
      descriptionEn: "Authorized Next Supply distribution partners",
      count: "50+ partners"
    },
    {
      icon: FiGlobe,
      title: isRTL ? "المنصات الإلكترونية" : "Online Platforms",
      titleEn: "Online Platforms",
      description: isRTL ? "مواقع التجارة الإلكترونية وتطبيقات التوصيل" : "E-commerce websites and delivery apps",
      descriptionEn: "E-commerce websites and delivery apps",
      count: "10+ platforms"
    }
  ];

  const contactInfo = {
    phone: "+20 103 151 2020",
    email: "info@nextsupplytd.com",
    address: isRTL ? "القاهرة، مصر" : "Cairo, Egypt"
  };

  return (
    <section id="distribution" className="py-20 bg-gradient-to-b from-green-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-green-800 mb-6">
            {getText('header.title', 'distribution.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {getText('header.subtitle', 'distribution.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {regions.map((region, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              className="bg-white rounded-2xl shadow-xl overflow-hidden"
            >
              <div className={`bg-gradient-to-r ${region.color} p-6 text-white`}>
                <div className="flex items-center justify-between mb-4">
                  <SafeIcon icon={FiMapPin} className="w-8 h-8" />
                  <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-semibold" dir="ltr">
                    {region.stores} {isRTL ? "متجر" : "stores"}
                  </span>
                </div>
                <h3 className="text-xl font-bold mb-2">{isRTL ? region.nameAr : region.name}</h3>
                <p className="text-sm opacity-90">{isRTL ? region.name : region.nameAr}</p>
              </div>

              <div className="p-6">
                <h4 className="font-semibold text-green-800 mb-3">{isRTL ? "متوفر في:" : "Available in:"}</h4>
                <div className="flex flex-wrap gap-2">
                  {region.cities.map((city, cityIndex) => (
                    <span
                      key={cityIndex}
                      className="bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full"
                      dir={isRTL ? "rtl" : "ltr"}
                    >
                      {city}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <h3 className="text-3xl font-bold text-green-800 text-center mb-12">
            {isRTL ? "أين تشتري بُنديجا" : "Where to Buy بُنديجا"}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {storeTypes.map((type, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-amber-50 to-green-50 p-6 rounded-xl shadow-lg text-center"
              >
                <div className="bg-green-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={type.icon} className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-xl font-bold text-green-800 mb-2">{isRTL ? type.title : type.titleEn}</h4>
                <p className="text-gray-600 mb-3">{isRTL ? type.description : type.descriptionEn}</p>
                <span className="inline-block bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full font-semibold" dir="ltr">
                  {type.count}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-green-600 to-green-800 rounded-2xl p-8 text-white"
          >
            <h3 className="text-2xl font-bold mb-6">{getText('retailer.title', 'distribution.retailer')}</h3>
            <p className="text-green-100 mb-6 leading-relaxed">
              {getText('retailer.text', 'distribution.description')}
            </p>
            <div className="space-y-4">
              <div className={`flex items-center space-x-3 ${isRTL ? 'space-x-reverse' : ''}`}>
                <SafeIcon icon={FiPhone} className="w-5 h-5 text-green-200" />
                <span dir="ltr">{contactInfo.phone}</span>
              </div>
              <div className={`flex items-center space-x-3 ${isRTL ? 'space-x-reverse' : ''}`}>
                <SafeIcon icon={FiMapPin} className="w-5 h-5 text-green-200" />
                <span>{contactInfo.address}</span>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-green-800 px-6 py-3 rounded-lg font-semibold mt-6 hover:bg-green-50 transition-colors"
            >
              {isRTL ? "اتصل بنا اليوم" : "Contact Us Today"}
            </motion.button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div>
              <h3 className="text-2xl font-bold text-green-800 mb-4">
                {isRTL ? "إحصائيات التوزيع" : "Distribution Statistics"}
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">{isRTL ? "إجمالي مواقع التجزئة" : "Total Retail Locations"}</span>
                  <span className="font-bold text-green-800" dir="ltr">520+</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">{isRTL ? "المحافظات المصرية" : "Egyptian Governorates"}</span>
                  <span className="font-bold text-green-800" dir="ltr">27</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">{isRTL ? "شركاء التوزيع" : "Distribution Partners"}</span>
                  <span className="font-bold text-green-800" dir="ltr">50+</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">{isRTL ? "التسليم اليومي" : "Daily Deliveries"}</span>
                  <span className="font-bold text-green-800" dir="ltr">1,000+</span>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 p-6 rounded-xl">
              <h4 className="font-bold text-green-800 mb-3">{isRTL ? "ميزة نكست سابلاي" : "Next Supply Advantage"}</h4>
              <ul className="space-y-2 text-gray-700">
                <li className={`flex items-center space-x-2 ${isRTL ? 'space-x-reverse' : ''}`}>
                  <SafeIcon icon={FiTruck} className="w-4 h-4 text-green-600" />
                  <span>{isRTL ? "شبكة لوجستية وطنية" : "Nationwide logistics network"}</span>
                </li>
                <li className={`flex items-center space-x-2 ${isRTL ? 'space-x-reverse' : ''}`}>
                  <SafeIcon icon={FiStar} className="w-4 h-4 text-green-600" />
                  <span>{isRTL ? "ضمان جودة المنتج" : "Quality assurance guarantee"}</span>
                </li>
                <li className={`flex items-center space-x-2 ${isRTL ? 'space-x-reverse' : ''}`}>
                  <SafeIcon icon={FiPhone} className="w-4 h-4 text-green-600" />
                  <span>{isRTL ? "دعم العملاء 24/7" : "24/7 customer support"}</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Distribution;